enum BankMovementStatus
{
    Uncategorized = "uncategorized",    // red: no analytic account yet
    MissingDocuments = "missing",       // yellow: categorized, but a required document or part of the amount is not linked
    Complete = "complete"               // teal
}

interface BankMovementLinkSummary
{
    links:BankMovementDocumentLink[];
    total:number;
    mismatch:boolean;
    remaining:number;
}

// The categorization state of a bank movement, shared by the movement rows, the detail panel and
// the pending counts of the bank list, so the three always tell the same colour.
class BankMovementState
{
    // Some movements keep an empty analytic account name without account: they are uncategorized
    static isCategorized(movement:BankEntry) : boolean {
        return BankMovementState.analyticAccountTitle(movement) != null;
    }

    static analyticAccountTitle(movement:BankEntry) : string {
        let title = movement.analyticAccount?.title ?? movement.analyticAccountName;
        return title != null && title.trim().length > 0 ? title : null;
    }

    static statusOf(movement:BankMovement) : BankMovementStatus {
        if (BankMovementState.isCategorized(movement) == false) return BankMovementStatus.Uncategorized;

        // Only accounts that require a link ask for documents: without any, or with links that
        // don't cover the whole movement yet
        if (movement.analyticAccount?.requiredLink != true) return BankMovementStatus.Complete;

        let summary = BankMovementState.linkSummary(movement);
        if (summary.links.length == 0 || summary.mismatch) return BankMovementStatus.MissingDocuments;

        return BankMovementStatus.Complete;
    }

    // The links newest first by their document's date (legacy links without it by createdAt), their
    // total and the part of the movement they don't cover yet
    static linkSummary(movement:BankMovement) : BankMovementLinkSummary {
        let links:BankMovementDocumentLink[] = [];
        let objects = movement.documentLink?.allObjects ?? [];
        for (let index = 0; index < objects.length; index++) links.push(objects[index] as BankMovementDocumentLink);

        links.sort(function(a, b){
            let ad = (a.date ?? a.createdAt)?.getTime() ?? Number.MAX_SAFE_INTEGER;
            let bd = (b.date ?? b.createdAt)?.getTime() ?? Number.MAX_SAFE_INTEGER;
            if (ad == bd) return a.identifier < b.identifier ? -1 : 1;
            return bd - ad;
        });

        let total = 0;
        for (let index = 0; index < links.length; index++) total += links[index].amount ?? 0;

        if (links.length == 0) return { links: links, total: 0, mismatch: false, remaining: 0 };

        // Sign convention may differ between movement and documents, compare magnitudes
        let mismatch = Math.abs(Math.abs(total) - Math.abs(movement.amount)) > 0.005;

        // Pending amount in the links' own sign convention, so |total + remaining| == |movement amount|
        let sign = total != 0 ? Math.sign(total) : Math.sign(movement.amount);
        let remaining = sign * Math.abs(movement.amount) - total;

        return { links: links, total: total, mismatch: mismatch, remaining: remaining };
    }

    static tagsOf(movement:BankEntry) : string[] {
        if (movement.tags == null || movement.tags.length == 0) return [];
        return movement.tags.split(",").map(t => t.trim()).filter(t => t.length > 0);
    }

    static setTags(movement:BankEntry, tags:string[]) {
        movement.tags = tags.length > 0 ? tags.join(",") : null;
    }

    // Amounts with a real minus sign, and incomes with a leading plus
    static signedAmountString(amount:number, plusForIncome:boolean = true) : string {
        let cf = (MUIWebApplication.sharedInstance().delegate as AppDelegate).currencyFormatter;
        let text = cf.stringFromNumber(Math.abs(amount ?? 0));
        if (amount < 0) return "−" + text;
        if (amount > 0 && plusForIncome) return "+" + text;
        return text;
    }
}
